<template>
  <div class="home-page">
    <section class="hero" id="hero">
      <div class="container hero-content">
        <div class="hero-carousel" v-if="carouselProducts.length > 0">
          <el-carousel
            :interval="3000"
            height="700px"
            arrow="always"
            :autoplay="true"
            indicator-position="outside"
          >
            <el-carousel-item
              v-for="(product, index) in carouselProducts"
              :key="product.prodId"
            >
              <div class="carousel-banner" :class="`gradient-${index % 5}`" @click="goToDetail(product.prodId)">
                <div class="banner-content">
                  <div class="banner-text">
                    <div class="banner-badge">HOT DEAL</div>
                    <h2 class="banner-title">{{ product.prodTitle }}</h2>
                    <p class="banner-subtitle" v-if="product.prodContent">{{ formatBannerDesc(product.prodContent) }}</p>
                    <div class="banner-price-section">
                      <div class="banner-price" v-if="product.prodPrice">
                        <span class="price-label">Only</span>
                        <span class="price-value">${{ product.prodPrice }}</span>
                      </div>
<!--                      <div class="banner-reviews" v-if="product.prodReview">-->
<!--                        ⭐ {{ product.prodReview }}+ Reviews-->
<!--                      </div>-->
                    </div>
                    <button class="banner-cta" @click.stop="goToDetail(product.prodId)">
                      Shop Now →
                    </button>
                  </div>
                  <div class="banner-image">
                    <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
                  </div>
                </div>
              </div>
            </el-carousel-item>
          </el-carousel>
        </div>
      </div>
    </section>

    <section class="category-highlight" id="category-highlight">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Core Product Selection</h2>
          <p class="section-subtitle">Curated star products for various scenarios, quickly building a high-conversion product pool.</p>
        </div>
        <div class="category-grid">
          <div
            class="category-card"
            v-for="category in categoryCards"
            :key="category.title"
            role="button"
            tabindex="0"
            @click="category.action && category.action()"
            @keyup.enter="category.action && category.action()"
          >
            <div class="card-media">
              <img :src="category.image" :alt="category.title" />
            </div>
            <div class="card-body">
              <h3>{{ category.title }}</h3>
              <div class="category-meta">
                <span class="price" v-if="category.price">${{ category.price }}</span>
<!--                <div class="rating" v-if="category.review">-->
<!--                  ★★★★★ <span>({{ category.review }})</span>-->
<!--                </div>-->
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="product-showcase" id="new-arrivals">
      <div class="container">
        <div class="section-heading">
          <div class="badge">NEW ARRIVALS</div>
          <h2 class="section-title">New Arrivals · Launch Special</h2>
          <p class="section-subtitle">Focusing on high-rated new products on the platform, with comprehensive upgrades from feature highlights to packaging experience.</p>
        </div>
        <div class="product-grid">
          <div class="product-card" v-for="product in newArrivals" :key="product.prodId" @click="goToDetail(product.prodId)" role="button" tabindex="0" @keyup.enter="goToDetail(product.prodId)">
            <div class="product-thumb">
              <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
              <span class="tag">New</span>
            </div>
            <div class="product-info">
              <h3>{{ product.prodTitle }}</h3>
              <div class="product-meta">
                <span class="price" v-if="product.prodPrice">${{ product.prodPrice }}</span>
<!--                <div class="rating" v-if="product.prodReview">-->
<!--                  ★★★★★ <span>({{ product.prodReview }})</span>-->
<!--                </div>-->
              </div>
            </div>
          </div>
        </div>
        <div class="section-action">
          <router-link to="/products" class="cta-button">Browse More New Arrivals</router-link>
        </div>
      </div>
    </section>

    <section class="best-selling" id="best-selling">
      <div class="container">
        <div class="section-heading">
          <div class="badge">TOP SELLERS</div>
          <h2 class="section-title">Best Sellers · Customer Favorites</h2>
          <p class="section-subtitle">Based on real sales and user feedback, carefully selected best-selling items to help maintain hot sales.</p>
        </div>
        <div class="best-grid">
          <div class="featured-card" v-if="bestSelling[0]" @click="goToDetail(bestSelling[0].prodId)">
            <img :src="getFirstImage(bestSelling[0].prodImgurl)" :alt="bestSelling[0].prodTitle" />
            <div class="overlay">
              <h3>{{ bestSelling[0].prodTitle }}</h3>
              <div class="meta">
                <span v-if="bestSelling[0].prodPrice">${{ bestSelling[0].prodPrice }}</span>
<!--                <span class="reviews" v-if="bestSelling[0].prodReview">{{ bestSelling[0].prodReview }}+ Reviews</span>-->
              </div>
            </div>
          </div>
          <div class="best-list">
            <div class="best-item" v-for="product in bestSelling.slice(1, 5)" :key="product.prodId" @click="goToDetail(product.prodId)">
              <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
              <div class="info">
                <h4>{{ product.prodTitle }}</h4>
                <div class="meta">
                  <span v-if="product.prodPrice">${{ product.prodPrice }}</span>
<!--                  <span class="reviews" v-if="product.prodReview">{{ product.prodReview }}+ Reviews</span>-->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="accessories" id="collections">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Bundle Sets · Scenario-Based Recommendations</h2>
          <p class="section-subtitle">Package core products with accessories to quickly replicate a complete experience.</p>
        </div>
        <div class="accessory-grid">
          <div
            class="accessory-card"
            v-for="product in collectionProducts"
            :key="product.prodId"
            role="button"
            tabindex="0"
            @click="goToDetail(product.prodId)"
            @keyup.enter="goToDetail(product.prodId)"
          >
            <div class="thumb">
              <img :src="getFirstImage(product.prodImgurl)" :alt="product.prodTitle" />
            </div>
            <div class="body">
              <h3>{{ product.prodTitle }}</h3>
              <div class="accessory-meta">
                <span class="price" v-if="product.prodPrice">${{ product.prodPrice }}</span>
<!--                <div class="rating" v-if="product.prodReview">-->
<!--                  ★★★★★ <span>({{ product.prodReview }})</span>-->
<!--                </div>-->
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- <section class="blog-section" id="blog">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Guides & Information</h2>
          <p class="section-subtitle">Read installation tutorials, usage tips and case studies to quickly create high-converting product selling points.</p>
        </div>
        <div class="blog-grid">
          <article class="blog-card" v-for="article in blogPosts" :key="article.title">
            <div class="badge">{{ article.tag }}</div>
            <h3>{{ article.title }}</h3>
            <p>{{ article.summary }}</p>
            <a :href="article.link" target="_blank" rel="noopener" class="link-button">
              Read More
            </a>
          </article>
        </div>
      </div>
    </section> -->

    <!-- <section class="support-section" id="support">
      <div class="container support-grid">
        <div class="support-card">
          <h3>Enterprise Cooperation</h3>
          <p>For bulk purchasing, ODM customization, and distribution partnerships, please contact our business manager. We provide solutions and quotes within 24 hours.</p>
          <router-link to="/contact" class="link-button">Contact Business Team</router-link>
        </div>
        <div class="support-card">
          <h3>Resource Download Center</h3>
          <p>Product specifications, high-resolution materials, certification documents and manuals are continuously updated for quick listing and promotion.</p>
          <a href="#" class="link-button">Go to Download</a>
        </div>
        <div class="support-card">
          <h3>After-Sales & Customer Support</h3>
          <p>Providing one-stop services including product consultation, usage guides, after-sales maintenance and warranty information.</p>
          <a class="link-button" @click="scrollToSection('faq')">View FAQ</a>
        </div>
      </div>
    </section> -->

    <!-- <section class="faq-section" id="faq">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">Frequently Asked Questions</h2>
          <p class="section-subtitle">Find answers about purchasing, payment, resources, logistics and after-sales here.</p>
        </div>
        <div class="faq-grid">
          <div class="faq-item" v-for="faq in faqs" :key="faq.q">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </div>
    </section> -->
  </div>
</template>

<script>
import { getFirstImageUrl } from '@/utils/format'
import { error as logError } from '@/utils/logger'

export default {
  name: 'CompanyHome',
  data() {
    return {
      products: [],
      faqs: [
        {
          q: 'How to proceed with bulk purchasing and customization?',
          a: 'Contact our business team, specify your target market, purchase quantity and packaging requirements. We can provide OEM/ODM services and exclusive quotes.'
        },
        {
          q: 'What payment and logistics methods are supported?',
          a: 'We support various payment methods including bank transfer and PayPal. International shipping available via DHL, UPS, FedEx, and SF Express International.'
        },
        {
          q: 'Can you provide product materials and marketing assets?',
          a: 'We can provide specification sheets, high-resolution images, short videos and certification documents, with multi-language localization support for quick listing.'
        },
        {
          q: 'What is the warranty and after-sales policy?',
          a: 'Complete units come with 12 months limited warranty, accessories with 6 months. Mail-in repair and replacement supported. See after-sales terms in payment guide for details.'
        }
      ]
    }
  },
  computed: {
    mainFeature() {
      return this.products[0] || null
    },
    mainFeatureImage() {
      return this.getFirstImage(this.mainFeature?.prodImgurl) || 'https://via.placeholder.com/420x480?text=Top+Product'
    },
    primaryProductName() {
      return this.mainFeature ? this.mainFeature.prodTitle : 'Best Seller'
    },
    secondaryFeatures() {
      return this.products.slice(1, 3)
    },
    carouselProducts() {
      if (this.products.length <= 10) {
        return this.products
      }
      // 随机选择10个商品
      const shuffled = [...this.products].sort(() => 0.5 - Math.random())
      return shuffled.slice(0, 10)
    },
    newArrivals() {
      return this.products
        .slice()
        .sort((a, b) => this.parseTime(b.createTime) - this.parseTime(a.createTime))
        .slice(0, 8)
    },
    bestSelling() {
      return this.products
        .slice()
        .sort((a, b) => this.parseNumber(b.prodReview) - this.parseNumber(a.prodReview))
        .slice(0, 5)
    },
    collectionProducts() {
      return this.products
        .slice()
        .sort((a, b) => this.parseNumber(b.prodPrice) - this.parseNumber(a.prodPrice))
        .slice(0, 4)
    },
    categoryCards() {
      const cards = this.products.slice(0, 3).map(product => ({
        title: product.prodTitle,
        desc: this.formatContent(product.prodContent),
        features: this.extractHighlights(product),
        image: this.getFirstImage(product.prodImgurl) || 'https://via.placeholder.com/420x320?text=Product',
        price: product.prodPrice,
        review: product.prodReview,
        action: () => this.goToDetail(product.prodId),
        cta: 'View Details'
      }))
      if (!cards.length) {
        cards.push({
          title: 'Best-Selling Products Selection',
          desc: 'Real-time synchronization with overseas platform best-seller rankings, quickly completing hot product structure.',
          features: ['Stable Supply Chain', 'Complete Specifications', 'Fast Shipping'],
          image: 'https://via.placeholder.com/420x320?text=Collection',
          action: () => this.goToProducts(),
          cta: 'Browse All Products'
        })
      }
      return cards
    },
    blogPosts() {
      return [
        {
          title: 'How to Quickly Evaluate Cross-Border Best-Selling Potential',
          summary: 'Organize key points of market research, competitor analysis and cost accounting to make product selection more efficient.',
          link: '#',
          tag: 'Selection Guide'
        },
        {
          title: 'How Supply Chain Improves Fulfillment Efficiency',
          summary: 'From inventory planning and warehouse layout to international logistics, organize full-chain collaboration strategies.',
          link: '#',
          tag: 'Supply Chain Insights'
        },
        {
          title: 'What Materials Are Needed Before New Product Launch?',
          summary: 'Complete breakdown of photography, product pages and customer service scripts to help teams efficiently launch new products.',
          link: '#',
          tag: 'Operations Tips'
        }
      ]
    }
  },
  mounted() {
    this.fetchProducts()
  },
  methods: {
    fetchProducts() {
      const params = {
        prodDomain: window.location.hostname.split('.')[1]
      }
      this.$axios.post('/asin/list', params).then(response => {
        if (response.data.code === 0) {
          this.products = response.data.data || []
        }
      }).catch(error => {
        logError('Error fetching products:', error)
      })
    },
    goToDetail(prodId) {
      if (!prodId) return
      const url = this.$router.resolve(`/product/${prodId}`).href
      window.open(url, '_blank')
    },
    goToProducts() {
      this.$router.push('/products')
    },
    formatContent(content) {
      if (!content) return 'Best-selling item, learn more about highlights and experience.'
      const text = content.replace(/<br\s*\/?>/gi, ' ').replace(/[,，;]/g, ' ')
      return text.split(' ').slice(0, 12).join(' ')
    },
    extractHighlights(product) {
      if (!product?.prodContent) return ['Quality Selection', 'Great Reviews', 'Best Value']
      const parts = product.prodContent.split('<br/>')
      return parts.filter(Boolean).slice(0, 3).map(part => part.split(/[，,]/)[0])
    },
    extractCollectionFeatures(product) {
      if (!product?.prodContent) return ['Multi-Item Bundle', 'Complete Set']
      return product.prodContent.split('<br/>').slice(0, 3).map(line => line.split(/[，,]/)[0])
    },
    scrollToSection(sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    parseTime(value) {
      if (!value) return 0
      const time = new Date(value).getTime()
      return Number.isNaN(time) ? 0 : time
    },
    parseNumber(value) {
      if (!value) return 0
      if (typeof value === 'number') return value
      const normalized = String(value).replace(/[^\d.]/g, '')
      const num = Number(normalized)
      return Number.isNaN(num) ? 0 : num
    },
    getFirstImage(imgurl) {
      return getFirstImageUrl(imgurl)
    },
    formatBannerDesc(content) {
      if (!content) return 'Premium quality product with excellent reviews'
      const text = content.replace(/<br\s*\/?>/gi, ' ').replace(/[,，;]/g, ' ')
      return text.split(' ').slice(0, 15).join(' ')
    },
  }
}
</script>

<style scoped>
.home-page {
  display: flex;
  flex-direction: column;
  gap: 80px;
}

.hero {
  padding: 80px 0;
  margin-top: 0;
  background: linear-gradient(135deg, rgba(11, 31, 59, 0.92) 0%, rgba(0, 113, 227, 0.8) 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1), transparent 55%);
  pointer-events: none;
}

.hero-content {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
}


.hero-carousel {
  width: 100%;
  max-width: 100%;
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.hero-carousel :deep(.el-carousel__container) {
  height: 700px;
  border-radius: var(--radius-xl);
}

.hero-carousel :deep(.el-carousel__item) {
  border-radius: var(--radius-xl);
  overflow: hidden;
}

.carousel-banner {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.carousel-banner.gradient-0 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.carousel-banner.gradient-1 {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.carousel-banner.gradient-2 {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.carousel-banner.gradient-3 {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.carousel-banner.gradient-4 {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.carousel-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}

.banner-content {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 60px;
  padding: 60px 80px;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #fff;
}

.banner-badge {
  display: inline-block;
  align-self: flex-start;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.banner-title {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 10.8rem;
}

.banner-subtitle {
  font-size: 1.15rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  max-width: 500px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.banner-price-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 12px 0;
}

.banner-price {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.price-label {
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.price-value {
  font-size: 3.5rem;
  font-weight: 900;
  color: #ffd700;
  text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
}

.banner-reviews {
  font-size: 1.05rem;
  font-weight: 600;
  color: #ffd700;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
}

.banner-cta {
  align-self: flex-start;
  padding: 18px 48px;
  background: #fff;
  color: #667eea;
  border: none;
  border-radius: 999px;
  font-size: 1.15rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  margin-top: 8px;
}

.banner-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  background: #ffd700;
  color: #667eea;
}

.banner-image {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  height: 100%;
  overflow: hidden;
}

.banner-image::before {
  content: '';
  position: absolute;
  width: 120%;
  height: 120%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.banner-image img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3));
  transition: transform 0.4s ease;
  position: relative;
  z-index: 1;
}

.carousel-banner:hover .banner-image img {
  transform: scale(1.08) rotate(2deg);
}

.hero-carousel :deep(.el-carousel__arrow) {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  width: 56px;
  height: 56px;
  font-size: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  border-radius: 50%;
  opacity: 0;
}

.hero-carousel:hover :deep(.el-carousel__arrow) {
  opacity: 1;
}

.hero-carousel :deep(.el-carousel__arrow:hover) {
  background: #fff;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
}

.hero-carousel :deep(.el-carousel__indicators) {
  bottom: -50px;
}

.hero-carousel :deep(.el-carousel__indicator) {
  padding: 0;
}

.hero-carousel :deep(.el-carousel__button) {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

.hero-carousel :deep(.el-carousel__indicator.is-active .el-carousel__button) {
  width: 32px;
  border-radius: 6px;
  background: #fff;
}

.section-heading {
  text-align: center;
  margin-bottom: 36px;
}

.heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 28px;
}

.category-card {
  display: grid;
  gap: 18px;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-base);
}

.category-card:hover,
.category-card:focus-visible {
  box-shadow: var(--shadow-md);
  transform: translateY(-4px);
}

.category-card .card-media {
  width: 100%;
  height: 200px;
  background: #f4f6fb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.category-card .card-media img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.category-card .card-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  justify-content: space-between;
}

.category-card .card-body h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.category-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
  color: var(--text-secondary);
}

.category-card ul li::before {
  content: '•';
  margin-right: 8px;
  color: var(--brand-accent);
}

.category-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  color: var(--text-secondary);
}

.category-meta .price {
  font-weight: 700;
  color: #dc2626;
}

.category-meta .rating {
  color: #f97316;
}

.category-meta .rating span {
  font-size: 0.85rem;
  margin-left: 6px;
  color: #f97316;
}

.product-showcase,
.best-selling,
.accessories,
.blog-section,
.support-section,
.faq-section {
  scroll-margin-top: 120px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-top: 28px;
}

.product-card {
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: var(--transition-base);
}

.product-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-md);
}

.product-thumb {
  position: relative;
  background: #f4f6fb;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 220px;
  padding: 18px;
}

.product-thumb img {
  max-height: 100%;
  width: auto;
}

.product-thumb .tag {
  position: absolute;
  top: 16px;
  left: 16px;
  background: var(--brand-accent);
  color: #fff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.product-info {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  justify-content: space-between;
}

.product-info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.product-info p {
  color: var(--text-secondary);
  line-height: 1.6;
}

.product-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  color: var(--text-secondary);
}

.product-meta .price {
  font-weight: 700;
  color: #dc2626;
}

.product-meta .rating {
  color: #f97316;
}

.product-meta .rating span {
  font-size: 0.85rem;
  margin-left: 6px;
  color: #f97316;
}

.section-action {
  margin-top: 32px;
  text-align: center;
}

.best-grid {
  display: grid;
  grid-template-columns: minmax(280px, 1.2fr) minmax(280px, 1fr);
  gap: 24px;
}

.featured-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
  min-height: 420px;
  cursor: pointer;
}

.featured-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.featured-card:hover img {
  transform: scale(1.05);
}

.featured-card .overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(11, 31, 59, 0.2) 0%, rgba(11, 31, 59, 0.85) 100%);
  color: #fff;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 14px;
}

.featured-card .overlay h3 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  margin: 0;
}

.featured-card .overlay .meta {
  display: flex;
  gap: 16px;
  font-size: 0.9rem;
  color: #fff;
  margin-bottom: 4px;
}

.featured-card .overlay .meta > span:first-child {
  color: #dc2626;
  font-weight: 700;
}

.featured-card .overlay .meta .reviews {
  color: #f97316;
  font-weight: 600;
}

.featured-card .labels {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.featured-card .labels span {
  background: rgba(255, 255, 255, 0.16);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
}

.best-list {
  display: grid;
  gap: 18px;
}

.best-item {
  display: flex;
  gap: 16px;
  background: #fff;
  padding: 18px;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: var(--transition-base);
}

.best-item:hover {
  transform: translateX(6px);
  box-shadow: var(--shadow-sm);
}

.best-item img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  background: #f5f7fb;
  border-radius: var(--radius-md);
}

.best-item .info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.best-item .info h4 {
  font-size: 1rem;
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.best-item .meta {
  display: flex;
  gap: 16px;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.best-item .meta > span:first-child {
  color: #dc2626;
  font-weight: 700;
}

.best-item .reviews {
  color: #f97316;
  font-weight: 600;
}

.accessory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 22px;
}

.accessory-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-base);
}

.accessory-card:hover,
.accessory-card:focus-visible {
  transform: translateY(-6px);
  box-shadow: var(--shadow-md);
}

.accessory-card .thumb {
  background: #f4f6fb;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.accessory-card img {
  max-height: 100%;
  width: auto;
}

.accessory-card .body {
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  justify-content: space-between;
}

.accessory-card .body h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.accessory-card ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
  color: var(--text-secondary);
}

.accessory-card ul li::before {
  content: '—';
  margin-right: 8px;
  color: var(--brand-accent);
}

.accessory-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  color: var(--text-secondary);
}

.accessory-meta .price {
  font-weight: 700;
  color: #dc2626;
}

.accessory-meta .rating {
  color: #f97316;
}

.accessory-meta .rating span {
  font-size: 0.85rem;
  margin-left: 6px;
  color: #f97316;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
}

.blog-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: var(--shadow-sm);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.blog-card h3 {
  font-size: 1.2rem;
  font-weight: 600;
}

.blog-card p {
  color: var(--text-secondary);
}

.support-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 22px;
  margin-top: 32px;
}

.faq-item {
  background: #fff;
  border-radius: var(--radius-lg);
  padding: 22px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: var(--shadow-sm);
}

.faq-item h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.faq-item p {
  color: var(--text-secondary);
  line-height: 1.7;
}

@media (max-width: 1024px) {
  .hero {
    padding-top: calc(64px + var(--header-offset-tablet));
    margin-top: calc(-1 * var(--header-offset-tablet));
  }

  .hero-carousel {
    max-width: 100%;
  }

  .hero-carousel :deep(.el-carousel__container) {
    height: 580px;
  }

  .banner-content {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 40px;
  }

  .banner-title {
    font-size: 2.2rem;
  }

  .banner-subtitle {
    font-size: 1rem;
  }

  .price-value {
    font-size: 2.5rem;
  }

  .banner-image {
    display: none;
  }

  .best-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .home-page {
    gap: 60px;
  }

  .hero {
    padding-top: calc(48px + var(--header-offset-mobile));
    margin-top: calc(-1 * var(--header-offset-mobile));
    padding-bottom: 64px;
  }


  .hero-carousel :deep(.el-carousel__container) {
    height: 520px;
  }

  .banner-content {
    padding: 32px 24px;
  }

  .banner-title {
    font-size: 1.8rem;
  }

  .banner-subtitle {
    font-size: 0.95rem;
  }

  .price-value {
    font-size: 2rem;
  }

  .banner-cta {
    padding: 14px 36px;
    font-size: 1rem;
  }

  .hero-carousel :deep(.el-carousel__arrow) {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .hero-carousel :deep(.el-carousel__indicators) {
    bottom: -40px;
  }

  .product-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

</style>
